import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { AuthUser, getCurrentUser, getGoogleLoginUrl, logoutSession } from '../services/authApi';
import { clearAuthToken, getAuthToken, setAuthToken } from '../services/authStorage';

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  loginWithGoogle: () => void;
  handleAuthCallback: (token: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(getAuthToken());
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const bootstrap = async () => {
      const currentToken = getAuthToken();
      if (!currentToken) {
        setIsLoading(false);
        return;
      }

      try {
        const me = await getCurrentUser(currentToken);
        setToken(currentToken);
        setUser(me);
      } catch (error) {
        clearAuthToken();
        setToken(null);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    void bootstrap();
  }, []);

  const loginWithGoogle = () => {
    window.location.href = getGoogleLoginUrl();
  };

  const handleAuthCallback = async (nextToken: string) => {
    setAuthToken(nextToken);
    setToken(nextToken);

    const me = await getCurrentUser(nextToken);
    setUser(me);
  };

  const logout = async () => {
    const currentToken = getAuthToken();

    try {
      if (currentToken) {
        await logoutSession(currentToken);
      }
    } finally {
      clearAuthToken();
      setToken(null);
      setUser(null);
    }
  };

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      isLoading,
      isAuthenticated: Boolean(token && user),
      loginWithGoogle,
      handleAuthCallback,
      logout,
    }),
    [user, token, isLoading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit etre utilise dans AuthProvider');
  }
  return context;
};
